<template>
  <div class="relative z-30">
    <MultiSelect
      append-to="self"
      ref="storeTypesMultiSelect"
      v-model="search.storeTypeIds"
      :placeholder="t('allStoreTypes')"
      option-label="name"
      option-value="id"
      :options="storeTypes.storeTypes"
      :max-selected-labels="isMobile ? 1 : 2"
      :show-toggle-all="false"
      :pt="pt"
    >
      <template #footer>
        <div
          class="relative z-10 -mt-[1px] border-t border-b border-x border-gray-700 bg-white px-6 py-4 flex items-center justify-center gap-x-4 -mx-[12px]"
        >
          <UButton
            @click="apply"
            variant="outline"
            class="px-5 text-trublue-700"
            :ui="{ rounded: 'rounded-3xl' }"
          >
            {{ t('applySelected') }}
          </UButton>
          <UButton
            @click="clearSelectedStoreTypes"
            variant="link"
            class="flex items-center no-underline text-trublue-700 gap-x-2 hover:no-underline"
            :ui="{ rounded: 'rounded-3xl' }"
          >
            <Icon name="mdi:close" class="w-4 h-4" />
            {{ t('clearAll') }}
          </UButton>
        </div>
      </template>
    </MultiSelect>
  </div>
</template>

<script setup lang="ts">
import type { MultiSelectPassThroughOptions } from 'primevue/multiselect'
import type { PassThrough } from 'primevue/ts-helpers'
import { onMounted, watch } from 'vue'

const { t } = useI18n()
const search = useSearchStore()
const storeTypes = useStoreTypesStore()
const storeTypesMultiSelect = ref()
const { isMobile } = useDevice()
const gtm = useGtm()


onMounted(() => {
  const targetNode = document.body

  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (
          node instanceof HTMLElement &&
          node.classList.contains('store-types-panel')
        ) {
          requestAnimationFrame(() => {
            node.style.position = 'fixed'
            node.style.top = 'calc(100% + 5px)'
            node.style.left = '0'
            node.style.marginLeft = '10px'
            node.style.minWidth = 'calc(100% + 1rem)'
            node.style.zIndex = '9999'
          })
        }
      }
    }
  })

  observer.observe(targetNode, {
    childList: true,
    subtree: true,
  })
})

function apply() {
  storeTypesMultiSelect.value.hide()

  gtm?.trackEvent({
    event: 'Store types applied',
    category: 'search',
    action: 'User applied store types',
    label: storeTypes.storeTypes
      .filter(type => search.storeTypeIds?.includes(type.id))
      .map(type => type.name)
      .join(', ')
  })
  search.performSearch()
}

function clearSelectedStoreTypes() {
  search.storeTypeIds = undefined
  storeTypesMultiSelect.value.hide()

  gtm?.trackEvent({
    event: 'Store types cleared',
    category: 'search',
    action: 'User cleared store types'
  })
  search.performSearch()
}

const pt = computed<PassThrough<MultiSelectPassThroughOptions>>(() => {
  return {
    root: {
      class: 'inline-flex items-center h-full justify-between text-sm relative w-full'
    },
    labelContainer: {
      class: [
        'overflow-hidden flex text-gray-600 grow basis-0 cursor-pointer items-center py-1.5'
      ]
    },
    trigger: {
      class: [
        'flex items-center justify-center shrink-0 basis-[24px] p-0',
        'bg-transparent text-gray-600'
      ]
    },
    dropdownIcon: {
      class: 'text-xs w-3 h-3'
    },
    label: ({ props }) => {
      const selectedCount = props.modelValue?.length ?? 0

      return {
        class: [
          'block truncate overflow-hidden whitespace-nowrap cursor-pointer text-ellipsis py-1.5',
          selectedCount < 2 && isMobile ? 'max-w-24' : ''
        ]
      }
    },
    panel: {
      class: [
        'fixed z-50 bg-white text-gray-600 border-0 shadow-lg',
        '!top-[calc(100%+5px)] !left-0 !ml-[10px]', // Force override: shift from left cleanly
        isMobile ? 'w-[90vw]' : 'min-w-[calc(100%+1rem)]'
      ]
    },
    wrapper: {
      class: [
        'max-h-[200px] overflow-auto bg-white text-gray-600',
        'border border-gray-700 shadow-lg',
        '!pt-[6px] !box-border',
        '!ml-[-12px] !mr-[-12px]'
      ]
    },
    list: {
      class: ['pb-3 list-none m-0 w-full']
    },
    item: ({ context }) => ({
      class: [
        'px-4 py-2.5 flex items-center gap-x-2 justify-between cursor-pointer hover:bg-trublue-100 text-sm overflow-hidden relative whitespace-nowrap'
      ]
    }),
    option: {
      class: ['order-0', 'truncate']
    },
    itemCheckbox: {
      root: 'order-1 mr-2 text-trublue text-sm',
      input: 'hidden'
    },
    closeButton: {
      class: ['hidden']
    }
  }
})
</script>
